// Outbound messaging: sending a typed message, resolving tool-confirmation cards, saving record
// edits (as a natural-language "Update ..." command), and facet selection. Everything here writes
// to the socket and/or appends to the local thread; the cards engine lives in `useChatWorkspace`.
import { useCallback } from 'react';
import { socketService } from '@/services/SocketService';
import { store } from '@/redux/common/store';
import { buildAgentContextMeta } from '@/redux/selectors/buildUIStateSnapshot';
import type { SamAction, SamFacet, SamModule } from '@/components/SAM/samActionTypes';
import { HIDDEN, MODULE_ID_FIELD, MODULE_NOUN, type Rec } from '@/components/chat/ChatPills';
import type { TextMessage } from '@/components/chat/MessageBubble';
import type { ConfirmMessage } from '@/components/chat/ConfirmCard';
import type { OnFacet, OnSave } from '@/components/chat/UXView';
import type { UploadedFile } from '@/redux/internal-services/agentApi';
import type { ChatMessage } from '../types';

interface UseChatMessagingOptions {
  assistantId: string;
  conversationId: string;
  ensureConversationId: () => string;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  threadRef: React.RefObject<HTMLDivElement | null>;
}

export interface ChatMessaging {
  /** Returns true when a message was actually queued (socket emit issued) — false on a no-op
   *  (empty turn, agent not yet selected, or offline — the last of which also appends an error
   *  bubble). Callers must not treat the turn as "sent" (e.g. show a typing indicator) on false. */
  send: (text: string, files?: UploadedFile[]) => boolean;
  /** Removes a failed user bubble and re-sends its text/files as a fresh turn. */
  retryMessage: (msg: TextMessage) => void;
  sendDecision: (msgId: string, action: SamAction, decision: 'approved' | 'rejected') => void;
  handleCardSave: OnSave;
  handleFacetSelect: OnFacet;
}

export function useChatMessaging({
  assistantId, conversationId, ensureConversationId, setMessages, setInput, threadRef,
}: UseChatMessagingOptions): ChatMessaging {
  const resolveConfirm = useCallback((msgId: string, decision: 'approved' | 'rejected') => {
    setMessages(prev => prev.map(m =>
      m.id === msgId && m.kind === 'confirm' ? { ...m, resolved: decision } as ConfirmMessage : m,
    ));
  }, [setMessages]);


  const send = useCallback((text: string, files: UploadedFile[] = []) => {
    // A turn is valid if there's text OR at least one attachment.
    if ((!text.trim() && !files.length) || !assistantId) return false;
    if (!socketService.isConnected) {
      setMessages(prev => [...prev, {
        kind: 'text', id: `err-${Date.now()}`, role: 'assistant', createdAt: Date.now(),
        text: "SAM is reconnecting — please try sending your message again in a moment.",
      } as TextMessage]);
      return false;
    }
    setInput('');
    const convId = ensureConversationId();
    const msgId = `u-${Date.now()}`;
    setMessages(prev => [...prev, {
      kind: 'text', id: msgId, role: 'user', text: text.trim(), createdAt: Date.now(),
      files: files.length ? files : undefined,
    } as TextMessage]);
    // Ack-based emit with a timeout: a message sent right as the access token expires can hit a
    // socket that's mid-teardown/reconnect (gateway ack never arrives) — without this the turn
    // silently vanishes (no error, no card) since plain `emit` is fire-and-forget. On timeout or a
    // gateway-reported error, mark the bubble failed so ChatThread renders an inline retry instead
    // of the user having to guess the message never sent.
    socketService.emitWithAck<{ ok: boolean }>('message', {
      type: 'sam.message',
      assistant_id: assistantId,
      message: text,
      conversation_id: convId,
      interaction_mode: 'text',
      files,
      // timezone/location (user_context) + current page/mode/view (ui), read at send time.
      meta: buildAgentContextMeta(store.getState()),
    }).then((ack) => {
      if (ack && ack.ok === false) {
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deliveryStatus: 'failed' } as TextMessage : m));
      }
    }).catch(() => {
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deliveryStatus: 'failed' } as TextMessage : m));
    });
    // Jump (no smooth animation) to the just-sent message; ChatThread keeps it pinned as the reply streams.
    setTimeout(() => threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight }), 50);
    return true;
  }, [assistantId, ensureConversationId, setMessages, setInput, threadRef]);

  const retryMessage = useCallback((msg: TextMessage) => {
    setMessages(prev => prev.filter(m => m.id !== msg.id));
    const files = (msg.files ?? []).filter((f): f is UploadedFile => !!f.type);
    send(msg.text, files);
  }, [send, setMessages]);

  const sendDecision = useCallback((msgId: string, action: SamAction, decision: 'approved' | 'rejected') => {
    socketService.emit('message', { type: 'sam.tool_decision', call_id: action.call_id, decision });
    resolveConfirm(msgId, decision);
  }, [resolveConfirm]);

  const handleCardSave: OnSave = useCallback((module: SamModule, id: string, draft: Rec) => {
    const skipKeys = new Set([...Array.from(HIDDEN), MODULE_ID_FIELD[module]]);
    const changes = Object.entries(draft)
      .filter(([k, v]) => !skipKeys.has(k) && typeof v !== 'object' && v !== null && v !== '')
      .map(([k, v]) => `${k.replace(/_/g, ' ')} = ${v}`).join(', ');
    send(`Update ${MODULE_NOUN[module]} with id ${id}: ${changes}`);
  }, [send]);

  const handleFacetSelect: OnFacet = useCallback((module: SamModule, facet: SamFacet) => {
    socketService.emit('message', { type: 'sam.facet_select', conversation_id: conversationId, module, key: facet.key, args: facet.args });
  }, [conversationId]);

  return { send, retryMessage, sendDecision, handleCardSave, handleFacetSelect };
}
